import { ReactNode } from 'react'

// ==========================================
// Tabs
// ==========================================

interface TabsProps {
    value: string
    onChange: (value: string) => void
    children: ReactNode
    className?: string
}

export const Tabs = ({ value, onChange, children, className = '' }: TabsProps) => {
    return (
        <div className={className}>
            <div role="tablist" className="flex gap-1 border-b border-slate-200 dark:border-slate-700">
                {Array.isArray(children) &&
                    children.map((child) => {
                        if (!child || typeof child !== 'object' || !('props' in child)) return null
                        const tabValue = child.props.value as string
                        const isActive = value === tabValue
                        return (
                            <button
                                key={tabValue}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => onChange(tabValue)}
                                className={`relative px-4 py-2 text-sm font-medium cursor-pointer transition-colors ${
                                    isActive
                                        ? 'text-blue-600 dark:text-blue-400'
                                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300'
                                }`}
                            >
                                <span className="flex items-center gap-2">
                                    {child.props.icon}
                                    {child.props.children}
                                </span>
                                {isActive && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />}
                            </button>
                        )
                    })}
            </div>
        </div>
    )
}

// ==========================================
// Tab (used as children of Tabs for declaration only)
// ==========================================

// `Tab` is a declaration-only component — its props are read by the parent
// `Tabs` via children introspection (`child.props.{value, icon, children}`).
// The props interface has to be declared explicitly so TypeScript accepts
// `<Tab value="..." icon={...}>Label</Tab>` at every call-site. Without it,
// TypeScript rejects `children` and rsbuild's `tsc --noEmit` breaks the build
// (see the 7 "Property 'children' does not exist on type 'IntrinsicAttributes'"
// errors the user hit in `AddUnitModal`, `FleetTabs`, `DeductionCardsSidebar`,
// `TruckDetailsModal`).
interface TabProps<TValue extends string = string> {
    value: TValue
    children?: ReactNode
    icon?: ReactNode
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const Tab = <TValue extends string = string>(_props: TabProps<TValue>) => {
    // Tab is rendered by Tabs via children introspection, not directly.
    return null
}

// ==========================================
// TabPanel
// ==========================================

interface TabPanelProps {
    value: string
    activeValue: string
    children: ReactNode
    className?: string
}

export const TabPanel = ({ value, activeValue, children, className = '' }: TabPanelProps) => {
    if (value !== activeValue) return null
    return (
        <div role="tabpanel" className={className}>
            {children}
        </div>
    )
}
